/**
 * BRAIN_REGISTRY — the dashboard's structural source of truth for the
 * six OMEGA brains.
 *
 * Per `docs/OMEGA-DASHBOARD-SPEC.md` §9.1: adding a 7th brain is a
 * single entry in this map plus a corresponding entry in the
 * engine-side projector. **No component file touches.** The L1
 * brain-status grid auto-flows; the L2 BrainPage reads its layout
 * from this registry; routes resolve `:brainId` against the keys
 * here.
 *
 * Order: matches the founder's canonical naming order (Knowledge,
 * Analytics, Decision, Production, Defense, Discovery). The L1 grid
 * is sorted by `position`; ties are broken alphabetically by `id`.
 */

import type { BrainId } from './types';

export interface BrainRegistryEntry {
  id: BrainId;
  /** Long-form name. Used in headings, breadcrumbs, page titles. */
  display_name: string;
  /** One-word short name. Used in the rail and tile chrome. */
  short_name: string;
  /** Lucide icon name. Reserved set — use existing icons only so the
   *  brand register stays consistent. */
  icon: string;
  /** L1 grid sort order; integer, lower-first. */
  position: number;
  /** One-sentence purpose. Surfaces in the tile hover + L2 header. */
  purpose: string;
}

export const BRAIN_REGISTRY: Record<BrainId, BrainRegistryEntry> = {
  knowledge: {
    id: 'knowledge',
    display_name: 'Knowledge',
    short_name: 'Knowledge',
    icon: 'BookOpen',
    position: 1,
    purpose:
      'Ingests, indexes, and recalls every signed entity OMEGA has ever observed.',
  },
  analytics: {
    id: 'analytics',
    display_name: 'Analytics',
    short_name: 'Analytics',
    icon: 'LineChart',
    position: 2,
    purpose:
      'Computes metrics, detects trends, and surfaces structural KPIs across brains.',
  },
  decision: {
    id: 'decision',
    display_name: 'Decision',
    short_name: 'Decision',
    icon: 'Gavel',
    position: 3,
    purpose:
      "Routes incoming work, applies the constitution, emits OMEGA's decisions.",
  },
  production: {
    id: 'production',
    display_name: 'Production',
    short_name: 'Production',
    icon: 'Factory',
    position: 4,
    purpose:
      'The Factory — six specialists that research, build, test, publish, and monitor products.',
  },
  defense: {
    id: 'defense',
    display_name: 'Defense',
    short_name: 'Defense',
    icon: 'Shield',
    position: 5,
    purpose:
      'Refuse-list enforcement, kill-switch readiness, attestor health, threat detection.',
  },
  discovery: {
    id: 'discovery',
    display_name: 'Discovery',
    short_name: 'Discovery',
    icon: 'Compass',
    position: 6,
    purpose:
      'Six labs (Combination, Pattern, Folding, Anomaly, Stress, Naming) that find what was missing.',
  },
};

/** Six brains in canonical display order. */
export const BRAINS_ORDERED: readonly BrainRegistryEntry[] = Object.values(
  BRAIN_REGISTRY,
).sort((a, b) =>
  a.position === b.position
    ? a.id.localeCompare(b.id)
    : a.position - b.position,
);

/** Lookup a brain by id; returns `undefined` for unknown ids. */
export function getBrain(id: string): BrainRegistryEntry | undefined {
  return BRAIN_REGISTRY[id as BrainId];
}
